(ns frontend.view.settings2.decisions.view
  (:require [clojure.set :as set]
            [clojure.string :as string]
            [common.localization :as loc]
            [common.schema.entity-type-names :as entity-type-names]
            [common.schema.entity-types :as entity-types]
            [common.schema.registry :as registry]
            [common.util :as cu]
            [frontend.actions.entity :as entity-actions]
            [frontend.page-components.default-page-layout :as default-page-layout]
            [frontend.page-components.zap-toolbar :as zap-toolbar]
            [frontend.routing :as routing]
            [frontend.state.current-session :as session]
            [frontend.view.not-found :as not-found]
            [frontend.view.settings2.decisions.events :as events]
            [frontend.view.settings2.decisions.sub :as sub]
            [frontend.view.settings2.sales-pipelines.db :as db]
            [re-frame.core :as rf]
            [reagent.core :as r]
            [ui.components.breadcrumbs.view :as breadcrumbs]
            [ui.components.button.view :as button]
            [ui.components.link.view :as link]
            [ui.components.modal.view :as modal]
            [ui.components.radix-ui.dropdown-menu.view :as dropdown-menu]
            [ui.components.toggle.view :as toggle]
            [ui.editors.data-input :as data-input]))

(defonce state (r/atom {}))
(defonce component-state (r/cursor state [:component-state]))

(defn submit-error-dialog
  []
  (let [{:keys [open? title description]} @(rf/subscribe [::sub/submit-error-dialog])]
    [modal/view
     {:visible? open?
      :hide!    #(rf/dispatch [::events/close-submit-error-dialog])}
     [:div
      title]
     [:div
      description]
     [modal/right-single-button
      nil
      false
      #(rf/dispatch [::events/close-submit-error-dialog])
      (loc/tr :ok)
      nil]]))

(defn breadcrumbs
  []
  [breadcrumbs/container
   [breadcrumbs/item
    {:on-click (fn [e]
                 (routing/set-view! [:account-settings :tab-key "config"]))}
    (loc/tr :settings/account-settings)]
   [breadcrumbs/item
    {:active? true}
    (loc/tr :settings/decisions)]])

(defn card
  [content]
  [:div
   content])

(defn decision-types-with-investment-reservations
  [tenant-schema]
  (->> entity-types/decision-entity-types
       (keep (fn [entity-type]
               (let [schema (registry/get-schema tenant-schema entity-type)
                     fields (keys schema)]
                 (when (some #{:decision/investment-reservations} fields)
                   entity-type))))
       (set)))

(def investment-reservation-configurable-fields
  [:investment-reservation/amount
   :investment-reservation/expiry-date
   :investment-reservation/void-date
   :investment-reservation/notes
   :investment-reservation/given-to
   :investment-reservation/given-by])

(defn decision-containing-entity-types
  []
  (cond-> []
          (session/dealflow?)
          (conj :entity.type/oppo)

          (session/exit-process-management?)
          (conj :entity.type/exit-opportunity)))

(defn investment-reservations-desc
  [tenant-schema]
  (let [decision-entity-types (decision-types-with-investment-reservations tenant-schema)
        decision-entity-types-str (->> decision-entity-types
                                       (map (fn [entity-type]
                                              (entity-type-names/get-plural entity-type :en))))
        entity-types-str (str (string/join ", " (butlast decision-entity-types-str))
                              " and "
                              (last decision-entity-types-str))]
    (str "Configure available investment reservation options for " entity-types-str)))

(defn investment-reservation-type-actions
  [investment-reservation-type]
  (let [id (:db/id investment-reservation-type)
        archived? (:common/archived? investment-reservation-type)
        disabled? (:common/disabled? investment-reservation-type)
        dropdown-items
        (concat
         (when-not archived?
           [{:label      (loc/tr :archive)
             :icon-class "fal fa-archive"
             :on-click   #(rf/dispatch [::events/archive-reservation-type id])}])
         (when archived?
           [{:label      (loc/tr :unarchive)
             :icon-class "fal fa-archive"
             :on-click   #(rf/dispatch [::events/unarchive-reservation-type id])}])
         [{:label      (loc/tr :delete)
           :icon-class "fal fa-trash"
           :on-click   #(rf/dispatch [::events/disable-reservation-type id])}])]

    [dropdown-menu/view
     {:trigger [:span
                [:i.fal.fa-ellipsis-v.text-2xl]]
      :items   dropdown-items}]))

(defn investment-reservations-configuration
  []
  (let [temp-title-state (r/atom {})]
    (fn []
      @temp-title-state
      (let [tenant-schema @session/tenant-schema
            show-archived-investment-reservation-types?
            @(rf/subscribe [::sub/show-archived-investment-reservation-types?])

            reservation-types @session/investment-reservation-types
            visible-reservation-types
            (->> reservation-types
                 (filter (fn [{:keys [common/archived?]}]
                           (or show-archived-investment-reservation-types?
                               (not archived?)))))
            archived-reservation-types (->> reservation-types
                                            (cu/filter-by :common/archived? true))
            add-reservation-type
            ;; The new row carries no title on purpose - the title cell is an
            ;; inline input saved on blur, so adding a row and typing into it is
            ;; the flow. Both callbacks used to be empty, which made a failed
            ;; create indistinguishable from a successful one: the button simply
            ;; appeared dead. Report both outcomes the way the archive/delete
            ;; actions in this namespace already do.
            (fn []
              (let [next-ord (->> reservation-types
                                  (keep :common/ord)
                                  (reduce max 0)
                                  (inc))
                    data {:entity/type                                          :entity.type/investment-reservation-type
                          :investment-reservation-type/subtract-from-dry-powder false
                          :common/ord                                           next-ord}]
                (events/create-investment-reservation-type!
                 data
                 (fn [_resp]
                   (rf/dispatch [::events/submit-success
                                 (loc/tr :added [(loc/tr :investment-reservation-type)])]))
                 (fn [err]
                   (rf/dispatch [::events/submit-error
                                 (loc/tr :add-reservation-type-failed)
                                 err])))))]
        [:div
         {:class "flex flex-col gap-3"}
         [:span
          {:class "text-xl font-bold"}
          (loc/tr :investment-reservations)]
         [:div
          [:div
           [:p
            {:class "text-base-light-text-paler"}
            (investment-reservations-desc tenant-schema)]]]

         [:div
          [button/sm-secondary-hollow
           {:on-click add-reservation-type
            :size     :small}
           [:i.fal.fa-plus]
           (loc/tr :add-reservation-type)]]
         [:div
          {:class "border border-solid border-base-light-border-paler rounded-lg overflow-x-auto"}
          [:div
           {:class "inline-block min-w-full"}
           [:table
            {:class "min-w-full divide-y divide-solid divide-base-light-border-paler"}
            [:thead
             [:tr
              {:class "*:px-3 *:py-3.5 *:text-left *:text-sm *:font-semibold *:text-base-light-text"}
              [:th (loc/tr :investment-reservation-type)]
              [:th (loc/tr :amount)]
              [:th (loc/tr :expiry-date)]
              [:th (loc/tr :void-date)]
              [:th (loc/tr :notes)]
              [:th (loc/tr :given-to)]
              [:th (loc/tr :given-by)]
              [:th (loc/tr :subtract-from-dry-powder)]
              (when (and (seq archived-reservation-types)
                         show-archived-investment-reservation-types?)
                [:th (str (loc/tr :archived) "?")])
              [:th ""]]]
            [:tbody
             {:class "divide-y divide-solid divide-base-light-border-paler"}
             (->>
              visible-reservation-types
              (sort-by :common/created-on)
              (map
               (fn [reservation-type]
                 (let [{:keys [db/id
                               common/archived?
                               investment-reservation-type/title
                               investment-reservation-type/enabled-fields
                               investment-reservation-type/subtract-from-dry-powder]}
                       reservation-type
                       entity-atom (r/cursor session/entities [id])
                       temp-title (or (get @temp-title-state id) title)

                       on-change
                       (fn [attr new-value old-value]
                         (swap! entity-atom assoc attr new-value)
                         (entity-actions/*->field-update! id
                                                          attr
                                                          new-value
                                                          (fn [resp])
                                                          (fn [err]
                                                            (js/console.log "error: " err)
                                                            (swap! entity-atom assoc attr old-value))))]
                   ^{:key (str "reservation-type-" id)}
                   [:tr
                    {:class "*:px-3 *:py-3 *:whitespace-nowrap *:text-sm *:text-base-light-text *:align-middle"}
                    [:td
                     [:div
                      [data-input/view

                       {:data-type :data.type/text
                        :ref       (fn [ref])
                        :class     ""
                        :value     temp-title
                        :on-change (fn [value]
                                     (swap! temp-title-state assoc id (str value)))
                        :on-blur   (fn [e]
                                     (on-change :investment-reservation-type/title temp-title title))}]]]
                    (->> investment-reservation-configurable-fields
                         (map (fn [field]
                                (let [enabled? (some #{field} enabled-fields)]
                                  ^{:key (str id "-field-" field)}
                                  [:td
                                   [:div
                                    {:class "inline-block"}
                                    [toggle/toggle
                                     {:checked?  enabled?
                                      :on-change (fn [toggled?]
                                                   (let [new-enabled-fields
                                                         (if toggled?
                                                           (set/union enabled-fields #{field})
                                                           (set/difference enabled-fields #{field}))]
                                                     (on-change :investment-reservation-type/enabled-fields
                                                                new-enabled-fields
                                                                enabled-fields)))}]]])))
                         (doall))
                    [:td
                     [:div
                      {:class "inline-block"}
                      [toggle/toggle
                       {:checked?  subtract-from-dry-powder
                        :on-change (fn [toggled?]
                                     (let [new-value
                                           (if toggled?
                                             true
                                             false)]
                                       (on-change :investment-reservation-type/subtract-from-dry-powder
                                                  new-value
                                                  subtract-from-dry-powder)))}]]]
                    (when (and (seq archived-reservation-types)
                               show-archived-investment-reservation-types?)
                      [:td
                       (if archived? (loc/tr :true) (loc/tr :false))])
                    [:td
                     [investment-reservation-type-actions reservation-type]]])))
              (doall))]]]]
         (when (seq archived-reservation-types)
           [:div
            [button/secondary-open
             {:on-click #(rf/dispatch [::events/set-show-archived-investment-reservation-types
                                       (not show-archived-investment-reservation-types?)])
              :size     :small}
             (if show-archived-investment-reservation-types?
               [:i.fal.fa-eye-slash]
               [:i.fal.fa-eye])
             (if show-archived-investment-reservation-types?
               (loc/tr :hide-archived)
               (loc/tr :show-archived))]])]))))

(defn decision-table-header
  []
  [:thead
   [:tr
    {:class "*:px-3 *:py-3.5 *:text-left *:text-sm *:font-semibold *:text-base-light-text"}
    [:th
     (loc/tr :decision-type)]
    [:th (loc/tr :enabled-for-entity-types)]]
   [:tr
    {:class "*:px-3 *:pb-3.5 *:text-left *:text-sm *:font-semibold *:text-base-light-text"}
    [:th (loc/tr :name)]
    (->> (decision-containing-entity-types)
         (map (fn [entity-type]
                ^{:key (str "table-header-" entity-type)}
                [:th (loc/tr entity-type)]))
         doall)]])

(defn decision-config-toggle
  [{:keys [decision-configuration decision-entity-type containing-entity-type
           on-save!]}]
  (let [enabled-containing-entity-types
        (get-in decision-configuration [:containing-entity-types decision-entity-type])
        enabled? (some #{containing-entity-type} enabled-containing-entity-types)]
    [:td
     [:div
      {:class "inline-block"}
      [toggle/toggle
       {:checked?  enabled?
        :on-change (fn [toggled?]
                     (let [enabled-containing-entity-types
                           (if toggled?
                             (set/union enabled-containing-entity-types
                                        #{containing-entity-type})
                             (set/difference enabled-containing-entity-types
                                             #{containing-entity-type}))]
                       (on-save! decision-entity-type enabled-containing-entity-types)))}]]]))

(defn decision-table-body
  [props]
  [:tbody
   {:class "divide-y divide-solid divide-base-light-border-paler"}
   (->>
    entity-types/decision-entity-types
    (sort-by (fn [decision-entity-type]
               (loc/tr decision-entity-type)))
    (map
     (fn [decision-entity-type]
       (let [decision-entity-name (loc/tr decision-entity-type)]
         ^{:key (str "config-row-" decision-entity-type)}
         [:tr
          {:class "*:px-3 *:py-4 *:whitespace-nowrap *:text-sm *:text-base-light-text *:align-middle"}
          [:td decision-entity-name]
          (->> (decision-containing-entity-types)
               (map (fn [containing-entity-type]
                      ^{:key (str containing-entity-type)}
                      [decision-config-toggle
                       (assoc
                        props
                         :decision-entity-type decision-entity-type
                         :containing-entity-type containing-entity-type)]))
               doall)])))
    doall)])

(defn decision-types-configuration
  []
  (let [decision-configuration @session/decision-configuration
        on-save! (fn [decision-entity-type enabled-containing-entity-types]
                   (let [old-config @session/decision-configuration
                         new-config (assoc-in decision-configuration
                                              [:containing-entity-types
                                               decision-entity-type]
                                              enabled-containing-entity-types)]
                     (reset! session/decision-configuration new-config)
                     (events/save-decision-configuration!
                      new-config
                      (fn [resp])
                      (fn [err]
                        (reset! session/decision-configuration old-config)
                        (js/console.log "Error saving decision config")))))]
    [:div
     {:class "flex flex-col gap-3"}
     [:span
      {:class "text-xl font-bold"}
      (loc/tr :settings.configuration/decisions)]
     [:div
      [:p
       {:class "text-base-light-text-paler"}
       (loc/tr :decision-containing-entity-config-desc)]]
     [:div
      {:class
       "border border-solid border-base-light-border-paler rounded-lg overflow-x-auto"}
      [:table
       {:class "min-w-full divide-y divide-solid divide-base-light-border-paler"}
       [decision-table-header]
       [decision-table-body
        {:decision-configuration decision-configuration
         :on-save!               on-save!}]]]]))

(def default-decision-config
  {:containing-entity-types      {:entity.type/ic-decision             #{}
                                  :entity.type/aifm-decision           #{}
                                  :entity.type/ic-exit-decision        #{}
                                  :entity.type/aifm-exit-decision      #{}
                                  :entity.type/advisory-board-decision #{}}
   :investment-reservation-types []})

(defn decisions-view
  []
  [:div
   {:class " grid grid-cols-1 md:grid-cols-10 flex flex-row"}
   [:div
    {:class "max-w-full md:col-span-2 sticky mb-6 top-0 flex md:flex-col gap-3 md:gap-1 md:gap-4
                               text-base-light-text font-semibold *:truncate"}
    [link/side-nav-link {:active?  (= :decisions
                                      (:selected-tab @component-state))
                         :on-click (fn []
                                     (swap! component-state assoc
                                            :selected-tab :decisions))}
     (loc/tr :settings/decisions)]

    [link/side-nav-link {:active?  (= :investment-reservations
                                      (:selected-tab @component-state))
                         :on-click (fn []
                                     (swap! component-state assoc
                                            :selected-tab :investment-reservations))}
     (loc/tr :investment-reservations2)]]
   [:div
    {:class "md:col-span-8 flex flex-col"}

    (case (:selected-tab @component-state)
      :decisions
      [card
       [decision-types-configuration]]
      :investment-reservations
      [card
       [investment-reservations-configuration]]
      [card
       [decision-types-configuration]])]])

(defn component
  []
  (swap! component-state assoc :selected-tab :decisions)
  (fn []
    [:<>
     [zap-toolbar/view db/state breadcrumbs nil nil]
     [:div
      {:class "bg-base-light text-base-light-text h-[calc(100vh_-_52px-_37px)] overflow-auto"}
      [:div
       {:class "px-4 py-6 mx-auto xl:w-5xl"}
       [decisions-view]
       [submit-error-dialog]]]]))

(defmethod routing/render-view :settings/decisions
  [_]
  (if (session/decisions?)
    [default-page-layout/view
     [component]]
    [not-found/not-found-view]))
